import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment, { Moment } from "moment";
import React, { useCallback, useMemo } from "react";
import DatePicker from "react-date-picker";
import { useIntl } from "react-intl";
import {
  MessageSchedulingChannel,
  ScheduledMessageContact,
  ScheduledMessageLink,
  ScheduledMessageMedia,
  linkHostname,
} from "../../utils/messageSchedulingStorage";

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from({ length: 12 }, (_, index) => index * 5);

interface Props {
  scheduledAt: string;
  onScheduledAtChange: (value: string) => void;
  channel: MessageSchedulingChannel;
  contacts: ScheduledMessageContact[];
  text: string;
  media: ScheduledMessageMedia[];
  link?: ScheduledMessageLink;
  onEditChannel: () => void;
  onEditContacts: () => void;
  onEditMessage: () => void;
}

/** Nunca deja avanzar con una fecha pasada: cae a "ahora + 1 h". */
function ensureFuture(candidate: Moment): Moment {
  const now = moment();
  if (candidate.isAfter(now)) {
    return candidate.clone().second(0).millisecond(0);
  }
  return now.clone().add(1, "hour").second(0).millisecond(0);
}

export const ScheduleStep = (props: Props) => {
  const {
    scheduledAt,
    onScheduledAtChange,
    channel,
    contacts,
    text,
    media,
    link,
    onEditChannel,
    onEditContacts,
    onEditMessage,
  } = props;
  const intl = useIntl();

  const current = useMemo(() => {
    const parsed = moment(scheduledAt);
    return parsed.isValid() ? parsed : moment().add(1, "hour");
  }, [scheduledAt]);

  const apply = useCallback(
    (next: Moment) => onScheduledAtChange(ensureFuture(next).toISOString()),
    [onScheduledAtChange]
  );

  const onDateChange = useCallback(
    (value: Date | Date[] | null) => {
      if (!value || Array.isArray(value)) return;
      apply(
        moment(value).hour(current.hour()).minute(current.minute())
      );
    },
    [apply, current]
  );

  const presets = useMemo(
    () => [
      {
        key: "in1h",
        label: intl.formatMessage({
          id: "app.messageScheduling.schedule.presetIn1h",
        }),
        value: () => moment().add(1, "hour"),
      },
      {
        key: "tomorrow9",
        label: intl.formatMessage({
          id: "app.messageScheduling.schedule.presetTomorrow",
        }),
        value: () => moment().add(1, "day").hour(9).minute(0),
      },
      {
        key: "nextMonday",
        label: intl.formatMessage({
          id: "app.messageScheduling.schedule.presetNextMonday",
        }),
        value: () =>
          moment().add(1, "week").isoWeekday(1).hour(9).minute(0),
      },
    ],
    [intl]
  );

  const summaryRows = useMemo(
    () => [
      {
        key: "channel",
        label: intl.formatMessage({
          id: "app.messageScheduling.step.channel",
        }),
        value: intl.formatMessage({
          id: `app.messageScheduling.channel.${channel}`,
        }),
        onEdit: onEditChannel,
      },
      {
        key: "contacts",
        label: intl.formatMessage({ id: "app.messageScheduling.recipients" }),
        value: contacts.length
          ? `${contacts
              .slice(0, 4)
              .map((contact) => contact.name)
              .join(", ")}${
              contacts.length > 4 ? ` (+${contacts.length - 4})` : ""
            }`
          : "—",
        onEdit: onEditContacts,
      },
      {
        key: "message",
        label: intl.formatMessage({ id: "app.messageScheduling.step.message" }),
        value:
          text.trim() ||
          media.map((item) => item.name).join(", ") ||
          (link?.url ? linkHostname(link.url) : "—"),
        onEdit: onEditMessage,
      },
    ],
    [
      channel,
      contacts,
      intl,
      link,
      media,
      onEditChannel,
      onEditContacts,
      onEditMessage,
      text,
    ]
  );

  const minuteValue = useMemo(
    () => Math.min(55, Math.round(current.minute() / 5) * 5),
    [current]
  );

  return (
    <div className="ms-schedule">
      <div className="ms-panel ms-panel--inner">
        <div className="ms-label">
          {intl.formatMessage({ id: "app.messageScheduling.schedule.label" })}
        </div>

        <div className="ms-schedule__datetime">
          <DatePicker
            value={current.toDate()}
            onChange={onDateChange}
            locale="es-ES"
            calendarType="ISO 8601"
            minDate={moment().startOf("day").toDate()}
            clearIcon={null}
            format="dd/MM/y"
            calendarIcon={
              <FontAwesomeIcon icon={["far", "calendar-days"] as any} />
            }
            calendarClassName="ms-calendar"
            className="ms-datePicker"
          />

          <div className="ms-schedule__time" role="group">
            <select
              className="ms-timeSelect"
              value={current.hour()}
              onChange={(event) =>
                apply(current.clone().hour(Number(event.target.value)))
              }
            >
              {HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {String(hour).padStart(2, "0")}
                </option>
              ))}
            </select>
            <span className="ms-schedule__timeSep">:</span>
            <select
              className="ms-timeSelect"
              value={minuteValue}
              onChange={(event) =>
                apply(current.clone().minute(Number(event.target.value)))
              }
            >
              {MINUTES.map((minute) => (
                <option key={minute} value={minute}>
                  {String(minute).padStart(2, "0")}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="ms-schedule__presets">
          {presets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className="ms-pill"
              onClick={() => apply(preset.value())}
            >
              <FontAwesomeIcon icon={["far", "clock"]} />
              {preset.label}
            </button>
          ))}
        </div>

        <div className="ms-schedule__resolved">
          <FontAwesomeIcon icon={["fas", "paper-plane"]} />
          {intl.formatMessage(
            { id: "app.messageScheduling.schedule.resolved" },
            {
              when: current.format("dddd DD/MM/YYYY HH:mm"),
              relative: current.fromNow(),
            }
          )}
        </div>
      </div>

      <div className="ms-panel ms-panel--inner ms-summary">
        <div className="ms-summary__title">
          {intl.formatMessage({ id: "app.messageScheduling.summary.title" })}
        </div>
        {summaryRows.map((row) => (
          <div key={row.key} className="ms-summaryRow">
            <span className="ms-summaryRow__label">{row.label}</span>
            <span className="ms-summaryRow__value">{row.value}</span>
            <button
              type="button"
              className="ms-summaryRow__edit"
              onClick={row.onEdit}
            >
              <FontAwesomeIcon icon={["fas", "pen"]} />
              {intl.formatMessage({ id: "app.messageScheduling.summary.edit" })}
            </button>
          </div>
        ))}
        <div className="ms-summaryRow">
          <span className="ms-summaryRow__label">
            {intl.formatMessage({ id: "app.messageScheduling.when" })}
          </span>
          <span className="ms-summaryRow__value">
            {current.format("DD/MM/YYYY HH:mm")}
          </span>
          <span className="ms-summaryRow__edit ms-summaryRow__edit--muted">
            {intl.formatMessage({
              id: "app.messageScheduling.summary.editHere",
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
